import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { ChildComponent } from 'src/app/components/child/child.component';
import {
  AppliedTermsService,
  getCombinedCategorySections,
  TermsSection,
} from 'src/app/services/modules/instant-rate-manager/applied-terms.service';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { SummernoteDirective } from 'src/app/directives/summernote.directive';

@Component({
  selector: 'app-applied-terms',
  templateUrl: './applied-terms.component.html',
  styleUrls: ['./applied-terms.component.scss'],
})
export class AppliedTermsComponent extends ChildComponent implements OnInit, AfterViewChecked {
  @ViewChild('termModalTemplate') termModalTemplate!: TemplateRef<any>;
  @ViewChild('combinedViewModalTemplate') combinedViewModalTemplate!: TemplateRef<any>;
  @ViewChildren(SummernoteDirective) summernoteInstances!: QueryList<SummernoteDirective>;

  private modalRef: any;
  private editorInitialized = false;

  mode: 'general' | 'category' = 'general';
  categoryId: string | null = null;
  pageTitle = 'General Terms & Conditions';
  listTitle = 'Your Terms';

  termsRecordId: string | null = null;
  sections: TermsSection[] = [];
  globalSections: TermsSection[] = [];
  editingSection: TermsSection | null = null;
  selectedSectionIndex: number | null = null;
  isCreatingNew = false;
  showGlobalReference = true;

  isLoading = false;
  isSaving = false;
  isSubmitted = false;
  isViewLoading = false;
  editorHeight = 360;

  viewGlobalSections: TermsSection[] = [];
  viewCategorySections: TermsSection[] = [];

  constructor(
    private appliedTermsService: AppliedTermsService,
    private rateManagerService: RateManagerService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.updateEditorHeight();
    this.route.paramMap.subscribe(params => {
      const categoryId = params.get('categoryId');
      this.mode = categoryId ? 'category' : 'general';
      this.categoryId = categoryId;
      this.cancelEdit();
      this.isSubmitted = false;

      if (this.mode === 'category' && categoryId) {
        this.loadCategoryName(categoryId);
      } else {
        this.pageTitle = 'General Terms & Conditions';
        this.listTitle = 'Your Terms';
      }

      this.loadTerms();
    });
  }

  ngAfterViewChecked(): void {
    if (!this.editorInitialized && this.editingSection && this.summernoteInstances.length) {
      this.editorInitialized = true;
      setTimeout(() => this.resetEditorDimensions(), 50);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateEditorHeight();
  }

  private updateEditorHeight() {
    this.editorHeight = window.innerWidth < 768 ? 260 : window.innerWidth < 1200 ? 320 : 360;
    this.resetEditorDimensions();
  }

  private async loadCategoryName(categoryId: string) {
    try {
      const response = await this.rateManagerService.getProviderRatesCategories({}, true);
      const category = (response?.data ?? []).find((c: any) => String(c.id) === categoryId);
      const name = category?.name
        ? category.name.charAt(0).toUpperCase() + category.name.slice(1)
        : 'Category';
      this.pageTitle = `${name} Terms & Conditions`;
      this.listTitle = `${name} Terms`;
      this.cdr.detectChanges();
    } catch {
      this.pageTitle = 'Category Terms & Conditions';
      this.listTitle = 'Category Terms';
    }
  }

  async loadTerms() {
    this.isLoading = true;
    this.termsRecordId = null;
    this.sections = [];
    this.globalSections = [];

    try {
      if (this.mode === 'general') {
        const record = await this.appliedTermsService.getGlobalTerms();
        if (record) {
          this.termsRecordId = record.id;
          this.sections = [...(record.sections ?? [])];
        }
      } else if (this.categoryId) {
        const full = await this.appliedTermsService.getCategoryTermsFull(this.categoryId);
        this.globalSections = [...(full?.global?.sections ?? [])];
        const categoryRecord = full?.category;
        if (categoryRecord) {
          this.termsRecordId = categoryRecord.id;
          this.sections = [...(categoryRecord.sections ?? [])];
        }
      }
    } catch (err: any) {
      AlertUtil.error(err?.error?.message || 'Failed to load terms and conditions.');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  openCreateModal() {
    this.isCreatingNew = true;
    this.isSubmitted = false;
    this.editingSection = this.createEmptySection();
    this.modalRef = this.modalService.open(this.termModalTemplate, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    this.modalRef.result.then(() => {}, () => this.cancelEdit());
  }

  selectSection(index: number) {
    this.isCreatingNew = false;
    this.isSubmitted = false;
    this.selectedSectionIndex = index;
    this.editingSection = { ...this.sections[index] };
    this.editorInitialized = false;
    this.cdr.detectChanges();
  }

  async saveSection() {
    if (!this.editingSection) return;

    this.isSubmitted = true;
    if (!this.editingSection.section_title?.trim()) return;
    if (this.isContentEmpty(this.editingSection.section_content)) return;

    const updatedSections = [...this.sections];

    const savedSection = this.buildSectionPayload(this.editingSection);

    if (this.isCreatingNew) {
      updatedSections.push(savedSection);
    } else if (this.selectedSectionIndex !== null) {
      updatedSections[this.selectedSectionIndex] = savedSection;
    }

    try {
      this.isSaving = true;
      await this.persistSections(updatedSections);
      this.sections = updatedSections;

      if (this.isCreatingNew) {
        this.modalRef?.close();
        AlertUtil.success('Term created successfully!');
      } else {
        AlertUtil.success('Term updated successfully!');
      }

      this.cancelEdit();
    } catch (err: any) {
      AlertUtil.error(err?.error?.message || err?.message || 'Failed to save term.');
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  private async persistSections(sections: TermsSection[]) {
    if (this.mode === 'general') {
      if (this.termsRecordId) {
        const record = await this.appliedTermsService.updateGlobalTerms(sections);
        this.termsRecordId = record.id;
      } else {
        const record = await this.appliedTermsService.createGlobalTerms(sections);
        this.termsRecordId = record.id;
      }
    } else if (this.categoryId) {
      if (this.termsRecordId) {
        const record = await this.appliedTermsService.updateCategoryTerms(this.categoryId, sections);
        this.termsRecordId = record.id;
      } else {
        const record = await this.appliedTermsService.createCategoryTerms(this.categoryId, sections);
        this.termsRecordId = record.id;
      }
    }
  }

  async deleteSection(index: number, event: Event) {
    event.stopPropagation();

    const result = await Swal.fire({
      title: 'Delete Term?',
      html: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        container: 'swal-on-top',
        confirmButton: 'btn btn-secondary text-white mx-2',
        cancelButton: 'btn btn-outline-primary mx-2',
        actions: 'd-flex justify-content-end gap-2',
      },
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const updatedSections = this.sections.filter((_, i) => i !== index);
          await this.persistSections(updatedSections);
          this.sections = updatedSections;
          return true;
        } catch (err: any) {
          Swal.showValidationMessage(err?.error?.message || 'Failed to delete term.');
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      AlertUtil.success('Term deleted successfully!');
      this.cancelEdit();
      this.cdr.detectChanges();
    }
  }

  isContentEmpty(content: string): boolean {
    if (!content) return true;
    const div = document.createElement('div');
    div.innerHTML = content;
    return !div.textContent?.trim();
  }

  cancelEdit() {
    this.editingSection = null;
    this.selectedSectionIndex = null;
    this.isCreatingNew = false;
    this.isSubmitted = false;
    this.editorInitialized = false;
    this.cdr.detectChanges();
  }

  toggleGlobalReference() {
    this.showGlobalReference = !this.showGlobalReference;
  }

  goToGeneralTerms() {
    this.goTo('/instant-rate-manager/applied-terms');
  }

  async openCombinedView() {
    if (!this.categoryId) return;

    this.isViewLoading = true;
    this.viewGlobalSections = [];
    this.viewCategorySections = [];

    const modalRef = this.modalService.open(this.combinedViewModalTemplate, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });

    try {
      const full = await this.appliedTermsService.getCategoryTermsFull(this.categoryId);
      this.viewGlobalSections = [...(full?.global?.sections ?? [])];
      this.viewCategorySections = getCombinedCategorySections(full?.category?.sections ?? []);
    } catch (err: any) {
      AlertUtil.error(err?.error?.message || 'Failed to load combined terms and conditions.');
      modalRef.dismiss();
    } finally {
      this.isViewLoading = false;
      this.cdr.detectChanges();
    }
  }

  get hasCombinedViewContent(): boolean {
    return this.viewGlobalSections.length > 0 || this.viewCategorySections.length > 0;
  }

  private resetEditorDimensions() {
    this.summernoteInstances?.forEach(instance => {
      instance.resetHeight(this.editorHeight);
    });
  }

  private createEmptySection(): TermsSection {
    const section: TermsSection = { section_title: '', section_content: '' };
    if (this.mode === 'category') {
      section.is_independent = false;
    }
    return section;
  }

  private buildSectionPayload(section: TermsSection): TermsSection {
    const payload: TermsSection = {
      section_title: section.section_title.trim(),
      section_content: section.section_content,
    };
    if (this.mode === 'category') {
      payload.is_independent = !!section.is_independent;
    }
    return payload;
  }
}
